import { useMemo } from "react";

import type { MarketingBannerRow } from "../types";

import { extractImageSrc, stripImpressionPixel } from "./helpers";

type Props = {
  banner: MarketingBannerRow;
  /** When omitted, the image fills its container with object-contain. */
  width?: number;
  /** When omitted, the image fills its container with object-contain. */
  height?: number;
  onLoad?: () => void;
};

/**
 * Renders an image banner by extracting the first <img src> from the banner's
 * creative HTML and rendering it directly via <img>. Falls back to nothing if
 * no src is found (the dispatcher in CreativePreview will swap to the unknown
 * fallback).
 */
export const ImageBannerPreview = ({
  banner,
  width,
  height,
  onLoad,
}: Props) => {
  // Strip the impression pixel first so a creative whose anchor accidentally
  // sits AFTER the pixel (defensive) won't surface the zero-pixel src.
  const src = useMemo(
    () => extractImageSrc(stripImpressionPixel(banner.creative_code)),
    [banner.creative_code],
  );

  if (!src) return null;

  // When explicit dimensions are provided, use them; otherwise fill container
  const hasDimensions = width !== undefined && height !== undefined;

  return (
    <img
      src={src}
      alt={banner.name}
      width={hasDimensions ? width : undefined}
      height={hasDimensions ? height : undefined}
      loading="lazy"
      onLoad={onLoad}
      className={
        hasDimensions
          ? "object-contain"
          : "max-h-full max-w-full object-contain"
      }
    />
  );
};
